import type { Options } from 'pusher-js';
import React from 'react';
import { Outlet } from 'react-router';
import { getSharedPusherClient } from 'typescript/lib/pusher';
import { PusherProvider } from '@/hooks/use-pusher';

function readPusherMetaTagConfig(): { clientKey: string; options: Options } | null {
  const pusherAppKey = document
    .querySelector('meta[name="pusher::app_key"]')
    ?.getAttribute('content');
  const pusherAppCluster = document
    .querySelector('meta[name="pusher::app_cluster"]')
    ?.getAttribute('content');
  const csrfToken = document
    .querySelector('meta[name="pusher::app_csrf"]')
    ?.getAttribute('content');

  if (!pusherAppKey || !csrfToken) return null;

  return {
    clientKey: pusherAppKey,
    options: {
      cluster: pusherAppCluster || 'mt1',
      forceTLS: true,
      channelAuthorization: {
        endpoint: '/pusher/auth',
        transport: 'ajax',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      },
    },
  };
}

/**
 * Root wrapper that provides a single Pusher client to every sqkt_app route.
 *
 * Prefer the shared Rails PusherController singleton so React does not open a
 * second websocket next to legacy jQuery. Fall back to a meta-tag client only
 * when that singleton is unavailable.
 */
function GlobalWrapper(): React.ReactNode {
  const sharedClient = getSharedPusherClient();

  if (sharedClient) {
    return (
      <PusherProvider client={sharedClient}>
        <Outlet />
      </PusherProvider>
    );
  }

  const metaConfig = readPusherMetaTagConfig();
  if (metaConfig) {
    return (
      <PusherProvider clientKey={metaConfig.clientKey} options={metaConfig.options}>
        <Outlet />
      </PusherProvider>
    );
  }

  return <Outlet />;
}

export { GlobalWrapper };
